import clsx from "clsx";
import { MessageSquare } from "lucide-react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useCreateChat } from "~/api/chat/chat";
import { Document, DocumentStatus, FolderChatStatus } from "~/api/model";
import { useGetProject } from "~/api/projects/projects";
import { DocumentStatusBadge } from "~/components/DocumentStatusBadge";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { TableCell, TableRow } from "~/components/ui/table";
import { formatBytes, ROUTES, useProjectParams } from "~/helpers";
import { isDocumentSelectable } from "../../helpers";

import { useDocumentSelectionStore } from "../../stores/documentSelectionStore";
import { DocumentImageName } from "./DocumentImage";
import { CaseDocumentActions } from "./DocumentsListItemActions";

type DocumentsListItemProps = {
  document: Document;
  allFilesNotProcessed: boolean;
  onCancelDraftUpload?: (filename: string) => void;
};

export const DocumentsListItem: React.FC<DocumentsListItemProps> = ({
  document,
  allFilesNotProcessed,
  onCancelDraftUpload,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const { mutateAsync: createChat } = useCreateChat();
  const { data: caseData } = useGetProject(organizationId, caseId);
  const navigate = useNavigate();

  // Get document selection from Zustand store
  const { isDocumentSelected, toggleDocumentSelection } =
    useDocumentSelectionStore();
  const isSelected = isDocumentSelected(document.id);

  const handleStartChat = async () => {
    const chat = await createChat({
      organizationId,
      projectId: caseId,
      params: { documentId: document.id },
      data: {
        documentIds: [document.id],
        title: document.filename,
      },
    });

    navigate(
      ROUTES.CHAT_DOCUMENT({
        organizationId,
        caseId,
        chatId: chat.id,
        searchParams: `?documentId=${document.id}`,
      })
    );
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDocumentSelectable(document, allFilesNotProcessed, caseData)) {
      toggleDocumentSelection(document.id);
    }
  };

  const isError = document.status === DocumentStatus.ERROR;
  const isCheckboxDisabled = !isDocumentSelectable(
    document,
    allFilesNotProcessed,
    caseData
  );
  const isStartChatDisabled =
    document.status !== DocumentStatus.COMPLETE ||
    allFilesNotProcessed ||
    caseData?.chatStatus !== FolderChatStatus.COMPLETE;

  return (
    <TableRow
      key={document.id}
      className={clsx(
        "text-zinc-500 !text-sm !font-medium leading-tight w-full grid grid-cols-12 gap-4",
        isError && "bg-gray-50 hover:bg-gray-100",
        !isStartChatDisabled && "cursor-pointer group"
      )}
      data-document-id={document.id}
    >
      <TableCell className="py-4 font-medium col-span-7 flex items-center">
        <div
          onClick={isCheckboxDisabled ? undefined : handleCheckboxClick}
          className={clsx(
            "pl-2 pr-4",
            isCheckboxDisabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
        >
          <Checkbox
            checked={isSelected}
            disabled={isCheckboxDisabled}
            aria-label="Select document"
            className={clsx(
              "data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border border-[#E4E4E7] shadow-none",
              isCheckboxDisabled
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer"
            )}
          />
        </div>
        <DocumentImageName document={document} />
      </TableCell>
      <TableCell className="py-4 col-span-1 flex items-center">
        <DocumentStatusBadge document={document} errorMessage={undefined} />
      </TableCell>
      <TableCell className="py-4 col-span-1 flex items-center">
        {document.pageCount || "-"}
      </TableCell>
      <TableCell className="py-4 col-span-1 flex items-center">
        {formatBytes(Number(document.uploadSize) || Number(document.size))}
      </TableCell>
      <TableCell
        className="py-4 text-right col-span-2 flex items-center justify-end"
        onClick={(e) => e.stopPropagation()}
      >
        {!isStartChatDisabled && (
          <Button
            variant="ghost"
            onClick={handleStartChat}
            className="invisible group-hover:visible transition-opacity duration-300 ease-in-out opacity-0 group-hover:opacity-100"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            <FormattedMessage
              id="caseDocument.actionsDropdown.chat"
              defaultMessage="Chat"
            />
          </Button>
        )}
        <CaseDocumentActions
          document={document}
          onCancelDraftUpload={onCancelDraftUpload}
        />
      </TableCell>
    </TableRow>
  );
};
